import { Progress, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import type { ReadmodelRow } from '../readmodelSource';
import type { ReadmodelWidgetState } from './readmodelState';
import { ReadmodelEmpty, ReadmodelLoading, ReadmodelUnavailable } from './ReadmodelWidgetStates';
import classes from './ProgressWidget.module.css';

/**
 * TV1-2291 (SDD §5/§6 · t25) — the `progress` tile: a completion bar over a batch
 * process.
 *
 * ## Which fields carry "done" and "total" is CONFIGURATION, not a convention
 *
 * The SDD says so explicitly (§5): *"Which fields carry 'done' and 'total' is
 * per-collection and lives in `widgetComponentProps`, so the schema stays
 * source-agnostic."* The defaults below (`completedCount` / `totalItems`) are
 * `batch_processes_readmodel`'s own field names — the motivating source, and the
 * only collection in `READMODEL_CATALOG` that declares a completion pair at all.
 *
 * ⚠️ A default that fits ONE collection is a confident-wrong-answer risk on every
 * other one, so it is paired with a hard failure: if neither field yields a finite
 * number on ANY row, the tile renders `unreadable` and NAMES the two fields it
 * looked for. It never renders a 0% bar. A progress bar sitting at zero is the most
 * plausible wrong answer this widget can give — it looks like a process that has not
 * started, which is a real state — so the misconfiguration has to be LOUD.
 */

/** `batch_processes_readmodel.completedCount` — the motivating source's own name. */
export const DEFAULT_COMPLETED_FIELD = 'completedCount';
/** `batch_processes_readmodel.totalItems`. */
export const DEFAULT_TOTAL_FIELD = 'totalItems';

export type ProgressSummary =
  /**
   * Neither field is a finite number on any row. The configuration does not match
   * the collection — see the header for why this must not degrade to 0%.
   */
  | { kind: 'unreadable'; completedField: string; totalField: string }
  /**
   * Measured. `ratio` is `null` when `total` is 0: the percentage is a division by
   * zero and is genuinely undefined, so the counts are shown alone rather than a
   * fabricated `0%` or `100%`. Same rule as `computeKpiDelta`'s ÷0 branch.
   */
  | { kind: 'measured'; completed: number; total: number; ratio: number | null };

const readNumber = ( row: ReadmodelRow, field: string ): number | null => {
  const raw = field.split( '.' ).reduce<unknown>(
    ( value, segment ) =>
      typeof value === 'object' && value !== null
        ? ( value as Record<string, unknown> )[segment]
        : undefined,
    row
  );

  if ( raw === null || raw === undefined || raw === '' ) return null;
  // PV3 returns JSON numbers as numbers, but a projected counter can arrive as a
  // numeric STRING from a raw-SQL column. Both are summable; a status name is not.
  const numeric = typeof raw === 'number' ? raw : Number( raw );
  return Number.isFinite( numeric ) ? numeric : null;
};

/**
 * Total the two counters across the returned rows.
 *
 * Summed rather than first-row: a `progress` card pointed at "the batch processes
 * running right now" legitimately gets several, and their aggregate completion is
 * the reading. A single-row filter is the same code path with one row.
 */
export const summarizeProgress = (
  rows: ReadmodelRow[],
  completedField: string,
  totalField: string
): ProgressSummary => {
  let completed = 0;
  let total = 0;
  let measured = false;

  for ( const row of rows ) {
    const rowCompleted = readNumber( row, completedField );
    const rowTotal = readNumber( row, totalField );

    if ( rowCompleted === null && rowTotal === null ) continue;

    measured = true;
    completed += rowCompleted ?? 0;
    total += rowTotal ?? 0;
  }

  if ( !measured ) return { kind: 'unreadable', completedField, totalField };

  return {
    kind: 'measured',
    completed,
    total,
    // ÷0 is undefined, not 0% and not 100%. `null` suppresses the percentage and
    // leaves the counts to carry the information.
    ratio: total > 0 ? Math.min( 1, Math.max( 0, completed / total )) : null
  };
};

const asFieldName = ( raw: unknown, fallback: string ): string =>
  typeof raw === 'string' && raw.trim() ? raw.trim() : fallback;

export type ProgressWidgetProps = {
  /** The resolved state — see `readmodelState.resolveReadmodelWidgetState`. */
  state: ReadmodelWidgetState;
  /** `widgetComponentProps.completedField`. Defaults to {@link DEFAULT_COMPLETED_FIELD}. */
  completedField?: unknown;
  /** `widgetComponentProps.totalField`. Defaults to {@link DEFAULT_TOTAL_FIELD}. */
  totalField?: unknown;
  /** Optional label under the bar (the card chrome already renders the title). */
  caption?: string;
  /** Explicit locale for deterministic tests; defaults to the runtime locale. */
  locale?: string;
};

export const ProgressWidget = ({ state, completedField, totalField, caption, locale }: ProgressWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  if ( state.kind === 'loading' ) return <ReadmodelLoading />;
  if ( state.kind === 'error' || state.kind === 'unavailable' ) return <ReadmodelUnavailable />;
  if ( state.kind === 'empty' ) {
    return (
      <ReadmodelEmpty
        title={t( 'widget_progress_empty_title', 'No process to track' )}
        description={t(
          'widget_progress_empty_description',
          'No row met the condition. Check the card filter.'
        )}
      />
    );
  }

  const resolvedCompletedField = asFieldName( completedField, DEFAULT_COMPLETED_FIELD );
  const resolvedTotalField = asFieldName( totalField, DEFAULT_TOTAL_FIELD );
  const summary = summarizeProgress( state.rows, resolvedCompletedField, resolvedTotalField );

  // The misconfiguration case. It NAMES the fields rather than drawing an empty bar
  // — see the header for why a 0% bar is the worst available answer here.
  if ( summary.kind === 'unreadable' ) {
    return (
      <ReadmodelEmpty
        title={t( 'widget_progress_unreadable_title', 'Progress could not be read' )}
        description={t(
          'widget_progress_unreadable_description',
          'No row carries "{{completedField}}" and "{{totalField}}". Set the two fields on this card.',
          { completedField: summary.completedField, totalField: summary.totalField }
        )}
      />
    );
  }

  const numberFormat = new Intl.NumberFormat( locale, { maximumFractionDigits: 0 });
  const percentText = summary.ratio === null
    ? null
    : `${ new Intl.NumberFormat( locale, { maximumFractionDigits: 0 }).format( summary.ratio * 100 ) }%`;

  return (
    <div className={classes.container} data-testid={componentsTestIds.progressWidget} data-readmodel-state={state.kind}>
      <div className={classes.readout}>
        {percentText === null ? null : (
          <Text component="div" className={classes.percent} data-testid={componentsTestIds.progressWidgetPercent}>
            {percentText}
          </Text>
        )}
        <Text component="div" className={classes.counts} data-testid={componentsTestIds.progressWidgetCounts}>
          {t( 'widget_progress_counts', '{{completed}} of {{total}}', {
            completed: numberFormat.format( summary.completed ),
            total: numberFormat.format( summary.total )
          })}
        </Text>
      </div>

      <Progress
        value={( summary.ratio ?? 0 ) * 100}
        size="lg"
        radius="xs"
        color="primary"
        data-testid={componentsTestIds.progressWidgetBar}
        aria-label={t( 'widget_progress_aria', 'Completion' )}
      />

      {/* Suppressed in a non-ready state — see review item 2 (2026-08-19): an empty
          tile shows its empty state and nothing else. */}
      {caption && state.kind === 'ready' ? (
        <Text component="div" className={classes.caption} data-testid={componentsTestIds.progressWidgetCaption}>
          {caption}
        </Text>
      ) : null}
    </div>
  );
};
